import * as React from 'react';
import {type LocalAudioTrack, type LocalVideoTrack, RoomEvent} from 'livekit-client';
import {useMaybeRoomContext, useMediaDeviceSelect} from "@livekit/components-react";
import {useKrispNoiseFilter} from "@livekit/components-react/krisp";
import {mergeProps} from "@livekit/components-react/src/utils";
import MediaDeviceSelectModal from "./MediaDeviceSelectModal";

// Dynamically check Krisp support
async function checkKrispSupport(): Promise<boolean> {
    try {
        const { isKrispNoiseFilterSupported } = await import('@livekit/krisp-noise-filter');
        return isKrispNoiseFilterSupported();
    } catch (e) {
        return false;
    }
}

/** @public */
export interface MediaDeviceSelectProps
    extends Omit<React.HTMLAttributes<HTMLUListElement>, 'onError'> {
    kind: MediaDeviceKind;
    onActiveDeviceChange?: (deviceId: string) => void;
    onDeviceListChange?: (devices: MediaDeviceInfo[]) => void;
    onDeviceSelectError?: (e: Error) => void;
    initialSelection?: string;
    /** will force the browser to only return the specified device
     * will call `onDeviceSelectError` with the error in case this fails
     */
    exactMatch?: boolean;
    track?: LocalAudioTrack | LocalVideoTrack;
    /**
     * this will call getUserMedia if the permissions are not yet given to enumerate the devices with device labels.
     * in some browsers multiple calls to getUserMedia result in multiple permission prompts.
     * It's generally advised only flip this to true, once a (preview) track has been acquired successfully with the
     * appropriate permissions.
     *
     * @see {@link MediaDeviceMenu}
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices | MDN enumerateDevices}
     */
    requestPermissions?: boolean;
    onError?: (e: Error) => void;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    /**
     * If true and kind is 'audioinput', also show audio output devices in the same modal
     */
    includeAudioOutput?: boolean;
    /**
     * Indicates if sound mode is enabled (not in concentrated mode)
     */
    isSoundMode?: boolean;
    /**
     * Callback to open the troubleshooting panel from the device modal
     */
    onOpenHelp?: () => void;
}

/**
 * The `MediaDeviceSelect` list all media devices of one kind.
 * Clicking on one of the listed devices make it the active media device.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <MediaDeviceSelect kind='audioinput' />
 * </LiveKitRoom>
 * ```
 * @public
 */
export const MediaDeviceSelectM: (
    props: MediaDeviceSelectProps & React.RefAttributes<HTMLUListElement>,
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<HTMLUListElement, MediaDeviceSelectProps>(
    function MediaDeviceSelect(
        {
            kind,
            initialSelection,
            onActiveDeviceChange,
            onDeviceListChange,
            onDeviceSelectError,
            exactMatch,
            track,
            requestPermissions,
            onError,
            isOpen,
            setIsOpen,
            includeAudioOutput,
            isSoundMode,
            onOpenHelp,
            ...props
        }: MediaDeviceSelectProps,
        ref,
    ) {

        const room = useMaybeRoomContext();
        const handleError = React.useCallback(
            (e: Error) => {
                if (room) {
                    // awkwardly emit the event from outside of the room, as we don't have other means to raise a MediaDeviceError
                    room.emit(RoomEvent.MediaDevicesError, e);
                }
                onError?.(e);
            },
            [room, onError],
        );

        // Check Krisp browser support
        const [isKrispSupported, setIsKrispSupported] = React.useState<boolean | undefined>(undefined);

        React.useEffect(() => {
            if (kind === 'audioinput') {
                checkKrispSupport().then(setIsKrispSupported);
            }
        }, [kind]);

        // Get Krisp noise filter state (only for audioinput)
        // Krisp est opt-in, jamais activé d'office : son réseau de neurones WASM
        // consomme ~15-20 % d'un cœur en continu (1,5-2 ms par trame de 10 ms,
        // chiffres Krisp), ce qui suffit à saturer les PC modestes caméra
        // allumée — son haché et bannière « connexion faible » alors que le
        // réseau est bon. Sans Krisp, la suppression de bruit native du
        // navigateur (noiseSuppression) reste active.
        const krisp = useKrispNoiseFilter();

        // Get devices for primary kind (used for onActiveDeviceChange callback)
        const {devices, activeDeviceId, setActiveMediaDevice, className} = useMediaDeviceSelect({
            kind,
            room,
            track,
            requestPermissions,
            onError: handleError,
        });

        // Explicit hooks for each device type — ensures each tab always gets the right devices
        const {devices: audioInputDevices, activeDeviceId: activeAudioInputId, setActiveMediaDevice: setActiveAudioInputDevice} = useMediaDeviceSelect({
            kind: 'audioinput',
            room,
            requestPermissions,
            onError: handleError,
        });

        const {devices: outputDevices, activeDeviceId: activeOutputId, setActiveMediaDevice: setActiveOutputDevice} = useMediaDeviceSelect({
            kind: 'audiooutput',
            room,
            requestPermissions,
            onError: handleError,
        });

        const {devices: videoDevices, activeDeviceId: activeVideoId, setActiveMediaDevice: setActiveVideoDevice} = useMediaDeviceSelect({
            kind: 'videoinput',
            room,
            requestPermissions,
            onError: handleError,
        });

        React.useEffect(() => {
            if (initialSelection !== undefined) {
                setActiveMediaDevice(initialSelection);
            }
        }, [setActiveMediaDevice]);

        React.useEffect(() => {
            if (typeof onDeviceListChange === 'function') {
                onDeviceListChange(devices);
            }
        }, [onDeviceListChange, devices]);

        React.useEffect(() => {
            if (activeDeviceId && activeDeviceId !== '') {
                onActiveDeviceChange?.(activeDeviceId);
            }
        }, [activeDeviceId]);

        const handleShowModal = () => setIsOpen(!isOpen);

        const handleActiveDeviceChange = async (deviceId: string, deviceKind?: MediaDeviceKind) => {
            try {
                const targetKind = deviceKind || kind;
                if (targetKind === 'audiooutput') {
                    await setActiveOutputDevice(deviceId, {exact: exactMatch});
                } else if (targetKind === 'videoinput') {
                    await setActiveVideoDevice(deviceId, {exact: exactMatch});
                } else if (targetKind === 'audioinput') {
                    await setActiveAudioInputDevice(deviceId, {exact: exactMatch});
                } else {
                    await setActiveMediaDevice(deviceId, {exact: exactMatch});
                }
            } catch (e) {
                if (e instanceof Error) {
                    console.log(e)
                    onDeviceSelectError?.(e);
                } else {
                    throw e;
                }
            }
        };
        // Merge Props
        const mergedProps = React.useMemo(
            () => mergeProps(props, {className}, {className: 'lk-list'}),
            [className, props],
        );

        const title = "Choisis tes appareils";

        // Filter out system default devices — always use explicit typed hooks
        const filteredAudioInputDevices = audioInputDevices.filter((device) =>
            device.deviceId !== 'default' && device.deviceId !== 'communications'
        );

        const filteredOutputDevices = outputDevices.filter((device) =>
            device.deviceId !== 'default' && device.deviceId !== 'communications'
        );

        const filteredVideoDevices = videoDevices.filter((device) =>
            device.deviceId !== 'default' && device.deviceId !== 'communications'
        );

        const defaultTabIndex = kind === 'videoinput' ? 2 : 0;

        // Don't show selector if no devices available at all
        if (filteredAudioInputDevices.length === 0 && filteredOutputDevices.length === 0 && filteredVideoDevices.length === 0) return null;

        return (
            <MediaDeviceSelectModal
                isOpen={isOpen}
                activeDeviceId={activeAudioInputId}
                devices={filteredAudioInputDevices}
                handleActiveDeviceChange={handleActiveDeviceChange}
                handleShowModal={handleShowModal}
                title={title}
                includeAudioOutput={true}
                audioOutputDevices={filteredOutputDevices}
                activeOutputDeviceId={activeOutputId}
                isSoundMode={isSoundMode}
                // Krisp noise filter props (only show if supported)
                krispEnabled={kind === 'audioinput' && isKrispSupported ? krisp.isNoiseFilterEnabled : undefined}
                krispPending={kind === 'audioinput' && isKrispSupported ? krisp.isNoiseFilterPending : undefined}
                onKrispToggle={kind === 'audioinput' && isKrispSupported ? krisp.setNoiseFilterEnabled : undefined}
                videoInputDevices={filteredVideoDevices}
                activeVideoInputId={activeVideoId}
                onOpenHelp={onOpenHelp}
                defaultTabIndex={defaultTabIndex}
            />
        );
    },
);